import {Card, FlagProps, Label, openDeleteDialog, UploadImage} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {useGetFileByIdQuery, useUploadFileMutation} from '@dms/api/base';
import {
  useClearBranchValueWithoutReferenceMutation,
  useSetBranchSettingValueMutation,
} from '@dms/api/settings';
import {BranchListItemResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {useToggle} from 'shared/web';

interface BranchCardProps {
  branch: BranchListItemResponseBody;
  flags: FlagProps[];
  documentImage: string | null;
  emailImage: string | null;
}

export function BranchCard(props: BranchCardProps) {
  const [isExpanded, toggleIsExpanded] = useToggle(false);
  const [uploadFile] = useUploadFileMutation();

  const [uploadBranchImage] = useSetBranchSettingValueMutation();
  const [clearBranchImage] = useClearBranchValueWithoutReferenceMutation();

  const {data: documentImage} = useGetFileByIdQuery(
    {fileId: props.documentImage ?? ''},
    {skip: isNil(props.documentImage)}
  );
  const {data: emailImage} = useGetFileByIdQuery(
    {fileId: props.emailImage ?? ''},
    {skip: isNil(props.emailImage)}
  );

  const handleUpload = async (
    file: File,
    settingId: 'tenant-logo_document' | 'tenant-logo_email'
  ) => {
    const data = await uploadFile({file}).unwrap();

    await uploadBranchImage({
      settingId,
      branchId: props.branch.id,
      valueRequestBody: {force: false, value: {fileId: data.fileId}},
    }).unwrap();

    return {id: data.fileId, url: data.fileUri};
  };

  const handleClearImage = (settingId: 'tenant-logo_document' | 'tenant-logo_email') => {
    openDeleteDialog({
      'data-testid': `branchCard-delete-${settingId}`,
      onConfirm: () =>
        clearBranchImage({
          settingId,
          branchId: props.branch.id,
        })
          .unwrap()
          .catch(handleApiError),
    });
  };

  const parameters: string[] = [
    [props.branch.city, props.branch.street].filter(isNotNil).join(' - '),
    `${i18n.t('page.settings.labels.userCount')}: ${props.branch.usersCount}`,
  ];

  return (
    <Card
      title={props.branch.marketingName}
      parameters={parameters}
      isExpandable
      isExpanded={isExpanded}
      flags={props.flags}
      onExpandButtonClick={toggleIsExpanded}
    >
      <HStack spacing={8}>
        <VStack>
          <Label
            tooltip={
              isNotNil(props.documentImage) ? i18n.t('page.settings.labels.logoTooltip') : undefined
            }
          >
            {i18n.t('page.settings.labels.documentLogo')}
          </Label>
          <UploadImage
            uploadFileRequest={(file) => handleUpload(file, 'tenant-logo_document')}
            aspectRatio="5/1"
            onDeleteClick={() => handleClearImage('tenant-logo_document')}
            value={
              documentImage && props.documentImage
                ? {id: props.documentImage, url: documentImage?.originalUri}
                : null
            }
            size="minHeight"
          />
        </VStack>

        <VStack>
          <Label
            tooltip={
              isNotNil(props.emailImage) ? i18n.t('page.settings.labels.logoTooltip') : undefined
            }
          >
            {i18n.t('page.settings.labels.emailLogo')}
          </Label>
          <UploadImage
            uploadFileRequest={(file) => handleUpload(file, 'tenant-logo_email')}
            onDeleteClick={() => handleClearImage('tenant-logo_email')}
            aspectRatio="5/1"
            value={
              emailImage && props.emailImage
                ? {id: props.emailImage, url: emailImage?.originalUri}
                : null
            }
            size="minHeight"
          />
        </VStack>
      </HStack>
    </Card>
  );
}
